import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMobileExpand from '../hooks/useMobileExpand';
import html2canvas from 'html2canvas';

function ReportIssueWidget({ token, selectedProperty, username, screenName }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null); // base64
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { success, message, ticket_id } or { error }
  const [capturingShot, setCapturingShot] = useState(false);
  const { showLabel, handleTap } = useMobileExpand();

  // Draggable state
  const [pos, setPos] = useState({ x: null, y: null });
  const dragRef = useRef(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  useEffect(() => {
    setPos({ x: window.innerWidth - 165, y: window.innerHeight - 120 });
  }, []);

  const dragStartPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5; // px — ignore micro-jitter below this

  const onMouseDown = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 44, e.clientY - dragOffset.current.y)),
      });
    };
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, []);

  const onTouchStart = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const touch = e.touches[0];
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
  };

  useEffect(() => {
    const onTouchMove = (e) => {
      if (!dragging.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStartPos.current.x;
      const dy = touch.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, touch.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 44, touch.clientY - dragOffset.current.y)),
      });
    };
    const onTouchEnd = () => { dragging.current = false; };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchend', onTouchEnd); };
  }, []);

  const handleIconClick = () => {
    if (hasMoved.current) return;
    if (handleTap()) captureAndOpen();
  };

  const captureAndOpen = useCallback(async () => {
    setCapturingShot(true);
    setResult(null);
    setDescription('');
    setIncludeScreenshot(true);
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.6,
        useCORS: true,
        logging: false,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });
      setScreenshot(canvas.toDataURL('image/jpeg', 0.7));
    } catch (err) {
      console.warn('Screenshot capture failed:', err);
      setScreenshot(null);
      setIncludeScreenshot(false);
    }
    setCapturingShot(false);
    setOpen(true);
  }, []);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch('/api/v1/support/raise-ticket', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          description: description.trim(),
          screenshot_base64: includeScreenshot ? screenshot : null,
          screen_name: screenName || 'Unknown',
          include_screenshot: includeScreenshot && !!screenshot,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ success: true, message: data.message, ticket_id: data.ticket_id });
        setDescription('');
      } else {
        setResult({ error: data.error || 'Failed to create ticket' });
      }
    } catch (err) {
      setResult({ error: err.message || 'Network error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (pos.x === null) return null;

  return (
    <>
      {/* ── Draggable Report Issue Button ── */}
      <div
        ref={dragRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onClick={handleIconClick}
        style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 9997,
          height: 40, borderRadius: 20, paddingLeft: 12, paddingRight: showLabel ? 14 : 12, minWidth: showLabel ? 140 : 40, justifyContent: 'center',
          background: 'linear-gradient(135deg, #ef4444, #f97316)',
          boxShadow: '0 4px 16px rgba(239,68,68,0.4)',
          display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          cursor: dragging.current ? 'grabbing' : 'grab',
          userSelect: 'none', transition: 'all 0.25s ease',
          opacity: capturingShot ? 0.5 : 1,
        }}
        title="Report an Issue"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        {showLabel && <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>Report Issue</span>}
      </div>

      {/* ── Modal ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9996,
          background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
        }} onClick={(e) => { if (e.target === e.currentTarget && !submitting) setOpen(false); }}>
          <div style={{
            width: '100%', maxWidth: 620, maxHeight: '90vh',
            background: '#fff', borderRadius: 16,
            boxShadow: '0 25px 80px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div style={{
              padding: '1rem 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #ef4444, #f97316)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>Report an Issue</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                    {selectedProperty ? `${selectedProperty.code} \u2014 ${selectedProperty.name}` : ''}
                  </div>
                </div>
              </div>
              <button onClick={() => !submitting && setOpen(false)} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
                color: '#fff', cursor: 'pointer', padding: '6px 10px', fontSize: '1.1rem', lineHeight: 1,
              }}>{'\u2715'}</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '1.2rem 1.5rem' }}>
              {/* Success state */}
              {result?.success ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>{'\u2705'}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#166534', marginBottom: 6 }}>{result.message}</div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1.5rem' }}>Your issue has been reported to the support team.</div>
                  <button onClick={() => setOpen(false)}
                    style={{ padding: '0.6rem 2rem', borderRadius: 8, border: 'none', background: '#166534', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
                    Close
                  </button>
                </div>
              ) : (
                <>
                  {/* Screenshot preview */}
                  {screenshot && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>Screenshot Preview</div>
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', maxHeight: 200 }}>
                        <img src={screenshot} alt="Screenshot" style={{ width: '100%', display: 'block' }} />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={includeScreenshot} onChange={e => setIncludeScreenshot(e.target.checked)} />
                        Include screenshot with ticket
                      </label>
                    </div>
                  )}

                  {/* Description */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>Issue Description <span style={{ color: '#ef4444' }}>*</span></div>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Describe the issue you are facing..."
                      rows={4}
                      disabled={submitting}
                      style={{
                        width: '100%', padding: '0.7rem 1rem', border: '2px solid #e2e8f0',
                        borderRadius: 10, fontSize: '0.92rem', outline: 'none', resize: 'vertical',
                        fontFamily: 'inherit', transition: 'border-color 0.15s', boxSizing: 'border-box',
                      }}
                      onFocus={e => { e.target.style.borderColor = '#ef4444'; }}
                      onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                  </div>

                  {/* Screen info */}
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                    <strong>Current Screen:</strong> {screenName || 'Unknown'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '1rem' }}>
                    <strong>User:</strong> {username || '-'}
                  </div>

                  {/* Error */}
                  {result?.error && (
                    <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                      {result.error}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer buttons */}
            {!result?.success && (
              <div style={{ display: 'flex', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', justifyContent: 'flex-end' }}>
                <button onClick={() => !submitting && setOpen(false)} disabled={submitting}
                  style={{ padding: '0.6rem 1.5rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
                  Cancel
                </button>
                <button onClick={handleSubmit} disabled={submitting || !description.trim()}
                  style={{
                    padding: '0.6rem 1.5rem', borderRadius: 8, border: 'none',
                    background: submitting || !description.trim() ? '#fca5a5' : '#ef4444',
                    color: '#fff', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
                    fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ReportIssueWidget;
